import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, Award, Music2, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { classes } from '../data/mock';

const Classes = () => {
  const [selectedClass, setSelectedClass] = useState(null);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">
              🎵 Music Education
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Music Classes
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from our comprehensive Hindustani classical music programs designed for all ages and skill levels
            </p>
          </div>
        </div>
      </section>

      {/* Classes Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {classes.map((classItem) => (
              <Card 
                key={classItem.id} 
                className={`group cursor-pointer transition-all duration-300 border-2 hover:shadow-xl ${
                  selectedClass === classItem.id 
                    ? 'border-blue-300 shadow-lg' 
                    : 'border-gray-200 hover:border-blue-200'
                }`}
                onClick={() => setSelectedClass(selectedClass === classItem.id ? null : classItem.id)}
              >
                <CardContent className="p-0">
                  <div className="bg-gradient-to-r from-blue-400 to-blue-500 p-6 text-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <Music2 className="h-8 w-8 mb-4" />
                        <h3 className="text-xl font-bold mb-2">{classItem.name}</h3>
                        <div className="flex flex-wrap gap-2 mb-4">
                          <Badge variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
                            {classItem.ageGroup}
                          </Badge>
                          <Badge variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
                            {classItem.level}
                          </Badge>
                        </div>
                      </div>
                      <ArrowRight className={`h-5 w-5 transition-transform ${selectedClass === classItem.id ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {classItem.description}
                    </p>
                    
                    <div className="flex items-center justify-between mb-6 text-sm">
                      <div className="flex items-center text-gray-600">
                        <Clock className="h-4 w-4 mr-2 text-blue-500" />
                        <span className="font-medium">{classItem.duration} per session</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Users className="h-4 w-4 mr-2 text-blue-500" />
                        <span className="font-medium">Small Groups</span>
                      </div>
                    </div>

                    {selectedClass === classItem.id && (
                      <div className="animate-in slide-in-from-top-2 duration-300">
                        <div className="border-t border-gray-200 pt-6 mb-6">
                          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                            <Award className="h-4 w-4 text-blue-500 mr-2" />
                            What You'll Learn
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {classItem.highlights.map((highlight, index) => (
                              <div key={index} className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                                <span>{highlight}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <Button 
                          asChild
                          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
                        >
                          <Link to="/contact">Book This Class</Link>
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Class Benefits */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Our Classes Stand Out
            </h2>
            <p className="text-xl text-gray-600">
              Experience the difference in our teaching approach
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white border-blue-100 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Small Class Sizes</h3>
                <p className="text-gray-600">
                  Personalized attention with maximum 6 students per class ensures quality learning
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-blue-100 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Authentic Curriculum</h3>
                <p className="text-gray-600">
                  Traditional Hindustani classical music techniques combined with modern teaching methods
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-blue-100 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Music2 className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Performance Opportunities</h3>
                <p className="text-gray-600">
                  Regular concerts and cultural events to showcase your learning and build confidence
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your Musical Journey?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Book a trial class and experience our teaching approach firsthand
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              asChild
              className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg"
            >
              <Link to="/contact">Book Trial Class</Link>
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              asChild
              className="border-white text-white hover:bg-white/10"
            >
              <Link to="/about">Learn About Our Teachers</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Classes;